import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

// L4.01 — Workflow definition catalogue.
// GET /v1/wf/definitions?entityType=&status= → { rows, total }

interface WfDefinition {
  readonly workflowDefinitionId: string;
  readonly workflowCode: string;
  readonly workflowName: string;
  readonly entityType: string;
  readonly moduleCode: string;
  readonly workflowStatus: string;
  readonly versionNumber: number;
}

const STATUSES = ['', 'DRAFT', 'ACTIVE', 'DEPRECATED'] as const;

export function WorkflowDefinitionListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<WfDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ rows: WfDefinition[]; total: number }>(`/v1/wf/definitions${q}`, { token })
      .then((r) => { if (alive) { setRows(r.rows); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, status]);

  return (
    <main aria-label="wf-definitions">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('wf.defList.title')}</h1>
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="wf-status" style={{ marginRight: 8, fontSize: 13 }}>{t('wf.common.status')}</label>
        <select id="wf-status" aria-label="status-filter" value={status}
                onChange={(e) => { setRows(null); setStatus(e.target.value); }}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('wf.common.all')}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="definitions-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.defList.empty')}</p>
      ) : (
        <DataTable<WfDefinition>
          ariaLabel="definitions-table"
          rows={rows}
          rowKey={(d) => d.workflowDefinitionId}
          rowTestId={(d) => `definition-row-${d.workflowDefinitionId}`}
          columns={[
            { key: 'code', header: t('wf.common.code'), render: (d) => <Link to={`/admin/wf/definitions/${d.workflowDefinitionId}`}>{d.workflowCode}</Link> },
            { key: 'name', header: t('wf.common.name'), render: (d) => d.workflowName },
            { key: 'entity', header: t('wf.common.entity'), render: (d) => d.entityType },
            { key: 'module', header: t('wf.defList.colModule'), render: (d) => d.moduleCode },
            { key: 'status', header: t('wf.common.status'), render: (d) => d.workflowStatus },
            { key: 'ver', header: t('wf.defList.colVer'), render: (d) => d.versionNumber },
          ]}
        />
      )}
    </main>
  );
}
